import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Box, BoxProps, Button, Collapse, styled, useTheme } from '@mui/material';
import { Fragment, useCallback, useState } from 'react';

import { useBreakpoint } from '../../hooks/useBreakpoint/useBreakpoint';

export interface ToggleOption<K extends string = string> {
  key: K;
  label: string;
}

export interface CollapsibleSectionProps<T extends readonly ToggleOption[]> {
  toggleOptions: T;
  renderContent: (activeView: T[number]['key']) => React.ReactNode;
  renderActions?: React.ReactNode;
  containerProps?: BoxProps;
  contentProps?: BoxProps;
  onToggle?: (activeView: T[number]['key']) => void;
}

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
  },
}));

export const CollapsibleSection = <T extends readonly ToggleOption[]>({
  toggleOptions,
  renderContent,
  renderActions,
  containerProps = {},
  contentProps = {},
  onToggle,
}: CollapsibleSectionProps<T>) => {
  const { isMobile } = useBreakpoint();
  const theme = useTheme();

  const [expandedSections, setExpandedSections] = useState<Set<T[number]['key']>>(new Set());

  const handleToggle = useCallback(
    (newView: T[number]['key']) => {
      setExpandedSections((prev) => {
        const newSet = new Set(prev);

        // On mobile, we want to be able to have multiple sections open at once
        // // On desktop, we want to only have one section open at a time
        if (isMobile) {
          if (newSet.has(newView)) {
            newSet.delete(newView);
          } else {
            newSet.add(newView);
          }
        } else {
          if (newSet.has(newView)) {
            newSet.clear();
          } else {
            newSet.clear();
            newSet.add(newView);
          }
        }

        return newSet;
      });

      onToggle && onToggle(newView);
    },
    [isMobile, onToggle]
  );

  const isExpanded = (key: T[number]['key']) => expandedSections.has(key);

  const getButtonColor = (buttonView: T[number]['key']) =>
    isExpanded(buttonView) ? theme.palette.primary.main : 'inherit';

  const getButtonIcon = (buttonView: T[number]['key']) =>
    isExpanded(buttonView) ? <KeyboardArrowUp /> : <KeyboardArrowDown />;

  const getButtonLabel = (option: T[number]) => (isExpanded(option.key) ? `Close ${option.label}` : option.label);

  return (
    <>
      <Box display="flex" justifyContent="space-between" {...containerProps}>
        <ButtonContainer>
          {toggleOptions.map((option, index) => (
            <Fragment key={option.key}>
              <Button
                key={option.key}
                variant="text"
                onClick={() => handleToggle(option.key)}
                endIcon={getButtonIcon(option.key)}
                sx={{
                  color: getButtonColor(option.key),
                  ml: index > 0 ? 2 : 0,
                  textTransform: 'none',
                }}
              >
                {getButtonLabel(option)}
              </Button>
              {isMobile ? (
                <Collapse in={isExpanded(option.key)} timeout="auto" unmountOnExit>
                  {renderContent(option.key)}
                </Collapse>
              ) : null}
            </Fragment>
          ))}
        </ButtonContainer>
        {renderActions}
      </Box>

      {!isMobile && (
        <Collapse in={expandedSections.size > 0} timeout="auto" unmountOnExit>
          <Box my={2.5} mx={4.5} {...contentProps}>
            {renderContent(Array.from(expandedSections)[0])}
          </Box>
        </Collapse>
      )}
    </>
  );
};
